import React, { useEffect, useState, useCallback, useRef } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { colors } from 'us-react-components/dist/constants/Styles';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import toast from 'react-hot-toast';
import {
  PlanService,
  MasterServiceAreaPlanPriceService,
  Plan,
  MasterServiceAreaPlanPrice,
  SitterPlanService,
  SitterPlan,
  UserVoucherService,
  CreditCardService,
  CreditCard,
  SitterProfileStatusService
} from 'us-web-services';
import { Button } from 'us-react-components/dist/Button/Button';
import { NotificationBanner } from 'us-react-components/dist/NotificationBanner/NotificationBanner';
import { ButtonSize, ButtonType } from 'us-react-components/dist/Button/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faShieldAlt } from '@fortawesome/pro-solid-svg-icons/faShieldAlt';
import { faMoneyBillWave } from '@fortawesome/pro-solid-svg-icons/faMoneyBillWave';
import { faCertificate } from '@fortawesome/pro-solid-svg-icons/faCertificate';
import { faTools } from '@fortawesome/pro-solid-svg-icons/faTools';
import { faHandsHelping } from '@fortawesome/pro-solid-svg-icons/faHandsHelping';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { useModal } from '../../modals/modalContext';
import { Storage } from '../../constants';
import { ModalName } from '../../modals/constants';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { usePurchaseFlow } from './usePurchaseFlow';
import { isPurchaseBlocked } from './purchaseEligibility';

const LOCK_ICON_URL = 'https://assets.urbansitter.com/static/img/icons/lock.png';
const ALLOW_PURCHASE_ON_STATUS_ERROR = false;

interface ApiError {
  response?: {
    data?: {
      errors?: Array<{
        message: string;
      }>;
    };
  }
}

type CardType = 'default' | 'american-express' | 'discover' | 'master-card' | 'visa';

const cardLabels: Record<CardType, string> = {
  'default': 'Card Number',
  'american-express': 'American Express Card',
  'discover': 'Discover Card',
  'master-card': 'Mastercard',
  'visa': 'Visa Card'
};

interface CreditCardError {
  data?: {
    errors?: Array<{
      message: string;
      code?: string;
    }>;
  };
  message?: string;
  status?: number;
}

declare global {
  interface Window {
      grecaptcha: {
        enterprise: {
          ready: (callback: () => void) => void;
          execute: (siteKey: string, options: { action: string }) => Promise<string>;
        }
      };
  }
}

export const BuyBackgroundCheckPage = (): JSX.Element => {
  const stripe = useStripe();
  const elements = useElements();
  const cookies = new Cookies();
  const navigate = useNavigate();
  const location = useLocation();
  const modal = useModal();

  const [isNux] = useState(() => new URLSearchParams(location.search).has('nux'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [defaultPlan, setDefaultPlan] = useState<Plan>();
  const [currentPlan, setCurrentPlan] = useState<SitterPlan>();
  const [voucherCode, setVoucherCode] = useState('');
  const [membershipDiscount, setMembershipDiscount] = useState<MasterServiceAreaPlanPrice>();
  const [creditCard, setCreditCard] = useState<CreditCard>();
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [shouldShowForm, setShouldShowForm] = useState(false);
  const [hasShownDiscountModal, setHasShownDiscountModal] = useState(false);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isModerationHold, setIsModerationHold] = useState(false);
  const purchaseInFlightRef = useRef(false);

  const {
    services: { tracker },
    stores: { user: userStore },
    services
  } = useDependencies();

  const { config } = services;

  const { executeRecaptcha } = useRecaptcha(config.RECAPTCHA_KEY || '');

  const { user } = userStore;

  const { t } = useSharedTranslation(namespaces.caregiver);

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: true
    },
    paymentMethodOrder: ['card', 'cashapp', 'google_pay', 'apple_pay']
  };

  const toastConfig = {
    success: { duration: 4000, position: 'top-right' as const },
    error: { duration: 5000, position: 'top-right' as const },
    warning: { duration: 4000, position: 'top-right' as const }
  };

  const showJsonErrorToast = (response: unknown, defaultMessage?: string): void => {
    const errorResponse = response as CreditCardError;
    let errorMessage: string;

    if (errorResponse?.data?.errors?.[0]) {
      errorMessage = errorResponse.data.errors[0].message;
    } else if (errorResponse?.message) {
      errorMessage = errorResponse.message;
    } else {
      errorMessage = defaultMessage || t('pages.sitter.buyBackgroundCheck.messages.somethingWrong');
    }

    toast.error(errorMessage, toastConfig.error);
  };

  const handlePaymentElementChange = (event: StripePaymentElementChangeEvent): void => {
    setIsPaymentElementReady(event.complete);

    if (event.value && event.value.type) {
      setSelectedPaymentMethodType(event.value.type);
    }
  };

  const getCardLabel = (type: string): string => cardLabels[type as CardType] || cardLabels.default;

  const initPaymentForm = (): void => {
    setIsDeleting(false);
  };

  const handleCreditCardUpdateFail = (error: CreditCardError): void => {
    setIsSubmitting(false);
    initPaymentForm();
    showJsonErrorToast(error);
  };

  const deleteCard = async (params: { userId: number; id: number }): Promise<void> => {
    try {
      await CreditCardService.delete(params.id, {
        params: { userId: params.userId }
      });
      setCreditCard({});
      initPaymentForm();
    } catch (error) {
      handleCreditCardUpdateFail(error as CreditCardError);
    }
  };

  const canRenew = useCallback(() => currentPlan?.end &&
          !currentPlan.recurringFee &&(new Date(currentPlan.end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) <= 30, [currentPlan]);

  const addSkipCookie = useCallback(() => {
    const expires = new Date();
    expires.setMinutes(expires.getMinutes() + 60);
    cookies.set(Storage.SKIP_ACCESS_CHECK, 'true', {
      expires,
      path: '/',
    });
  }, []);

  const fetchMasterServiceAreaPlanPrices = async (): Promise<void> => {
    try {
      const response = await MasterServiceAreaPlanPriceService.getByFilter({
        params: {
          userId: user.id,
          sitterId: user.id
        }
      });

      const msaPrices = response.data.data;
      for (let i = 0; i < msaPrices.length; i++) {
        if (msaPrices[i].plan?.masterServiceAreaDiscount) {
          setMembershipDiscount(msaPrices[i]);
          modal?.setModalName(ModalName.MembershipDiscount);
          break;
        }
      }
    } catch (error) {
      showJsonErrorToast(error);
    }
  };

  const setPlans = async (): Promise<void> => {
    try {
      const response = await PlanService.getByFilter({
        params: {
          fields: 'discount',
          sitterId: user.id,
          userId: user.id
        }
      });

      const plans = response.data.data;
      setDefaultPlan(plans[plans.length - 1]);
    } catch (error) {
      showJsonErrorToast(error);
    }
  };

  const handleDeleteCard = async (): Promise<void> => {
    if (isModerationHold) {
      return;
    }

    if (!isDeleting && creditCard?.id) {
      setIsDeleting(true);
      if (user.id) {
        await deleteCard({ userId: user.id, id: creditCard.id });
      }
    }
  };

  const redeemVoucher = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await UserVoucherService.create({
        voucher: { code: voucherCode },
        userId: user.id
      });

      await setPlans();
      toast.success(t('pages.sitter.buyBackgroundCheck.messages.promoCreditApplied'));
      setVoucherCode('');
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError?.response?.data?.errors?.[0]) {
        toast.error(apiError.response.data.errors[0].message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMsaDiscount = (): number => defaultPlan?.masterServiceAreaDiscount || 0;

  const getDiscountCode = (): string => defaultPlan?.discountCode || t('pages.sitter.buyBackgroundCheck.messages.discountApplied');

  const getDiscount = (): number => defaultPlan?.discount || 0;

  const getSubtotal = (): number => defaultPlan?.fee || 0;

  const getTotal = (): string => {
    if (!defaultPlan) return '0.00';
    const msaDiscount = getMsaDiscount();
    const total = (defaultPlan.fee || 0) - (defaultPlan.discount || 0) - msaDiscount;
    return Math.max(total, 0).toFixed(2);
  };

  const handleSkip = (): void => {
    setIsSubmitting(true);
    navigate({
      pathname: Routes.sitter.jobBoard,
      search: isNux ? `${createSearchParams({ nux: 'true' })}` : '',
    });
  };

  const handlePurchase = async (): Promise<void> => {
    if (purchaseInFlightRef.current) {
      return;
    }

    purchaseInFlightRef.current = true;

    try {
      let blocked: boolean;

      try {
        blocked = await fetchPurchaseBlocked();
      } catch (error) {
        console.warn('[BuyBackgroundCheckPage] purchase eligibility re-check failed', error);
        showJsonErrorToast(error);

        return;
      }

      setIsModerationHold(blocked);

      if (blocked) {
        return;
      }

      await submitPayment();
    } finally {
      purchaseInFlightRef.current = false;
    }
  };

  const canSubmit = (): boolean => {
    if (isModerationHold) {
      return false;
    }

    if (isSubmitting) {
      return false;
    }

    if (!termsAccepted) {
      return false;
    }

    if (creditCard?.id || getTotal() === '0.00') {
      return true;
    }

    return isPaymentElementReady;
  };

  const getReturnUrl = (): string => {
    const searchParams = createSearchParams({
      purchased: 'true',
      ...(isNux ? { nux: 'true' } : {})
    });

    return `${window.location.origin}/app${Routes.backgroundCheckFinish}?${searchParams}`;
  };

  const handlePurchaseSuccess = (): void => {
    const searchParams = createSearchParams({
      purchased: 'true',
      ...(isNux ? { nux: 'true' } : {})
    });

    navigate(`${Routes.backgroundCheckFinish}?${searchParams}`, { replace: true });
  };

  const { submitPayment } = usePurchaseFlow({
    stripe,
    elements,
    user,
    defaultPlan,
    creditCard,
    selectedPaymentMethodType,
    total: getTotal(),
    returnUrl: getReturnUrl(),
    executeRecaptcha,
    tracker,
    t,
    setIsSubmitting,
    onCardSaved: setCreditCard,
    onSuccess: handlePurchaseSuccess,
    onError: showJsonErrorToast
  });

  const fetchCreditCards = async (): Promise<void> => {
    try {
      const response = await CreditCardService.findByFilter({
        params: {
          userId: user.id
        }
      });

      if (response.data.data.length > 0) {
        setCreditCard(response.data.data[0]);
        if (!selectedPaymentMethodType) {
          setSelectedPaymentMethodType('card');
        }
        setIsPaymentElementReady(true);
      }
    } catch (error) {
      showJsonErrorToast(error);
    }
  };

  const fetchPurchaseBlocked = async (): Promise<boolean> => {
    if (!user.id) {
      throw new Error('Cannot check purchase eligibility without a user id');
    }

    const response = (await SitterProfileStatusService.get(user.id)).data;

    return isPurchaseBlocked(response.data);
  };

  const refreshModerationHold = async (): Promise<boolean> => {
    try {
      const blocked = await fetchPurchaseBlocked();

      setIsModerationHold(blocked);

      return blocked;
    } catch (error) {
      console.warn('[BuyBackgroundCheckPage] purchase eligibility check failed', error);

      return ALLOW_PURCHASE_ON_STATUS_ERROR;
    }
  };

  const checkPlanAndRedirect = async (): Promise<boolean> => {
    try {
      const response = await SitterPlanService.getByFilter({
        params: {
          active: true,
          userId: user.id
        }
      });
      const plan = response.data.data[0];
      setCurrentPlan(plan);

      if (plan && !canRenew()) {
        navigate('/finish-background-check?purchased=true', { replace: true });
      } else {
        addSkipCookie();

        await setPlans();
        await fetchMasterServiceAreaPlanPrices();
        await fetchCreditCards();

        return await refreshModerationHold();
      }
    } catch (error) {
      showJsonErrorToast(error);
    }

    return false;
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleDeleteCard();
    }
  }, [handleDeleteCard]);

  useEffect(() => {
    if (membershipDiscount && !hasShownDiscountModal) {
      modal?.setModalName(ModalName.MembershipDiscount);
      setHasShownDiscountModal(true);
    }
  }, [membershipDiscount, modal, hasShownDiscountModal]);

  useEffect(() => {
    const initializeComponent = async (): Promise<void> => {
      setIsLoading(true);
      setShouldShowForm(true);

      if (new URLSearchParams(location.search).get('purchased') === 'error') {
        toast.error(t('pages.sitter.buyBackgroundCheck.messages.paymentFailed'), {
          ...toastConfig.error,
          duration: 8000
        });

      }

      const blocked = await checkPlanAndRedirect();

      setIsLoading(false);

      tracker.track('viewed-paywall', { moderationHold: blocked }, 'sitter-viewed-paywall');
    };

    initializeComponent();

    return (): void => {
      modal?.setModalName(undefined);
    };
  }, [isNux, user.id]);

  const isCardRemovalDisabled = isDeleting || isModerationHold;
  const isVoucherRedemptionDisabled = !voucherCode || isSubmitting || isModerationHold;

  return (
    <div className='min-h-screen bg-white relative'>
      {/* Full-screen background for desktop */}
      <div className='hidden lg:block absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-teal-500 to-blue-500' />
      <div className='container mx-auto py-10 min-h-[600px] relative'>
        {isLoading ? (
          <div className='flex flex-col lg:flex-row' style={{ minHeight: '600px' }}>
            <div className='lg:w-1/2 p-8 lg:py-10 mb-12 lg:mb-0 rounded-lg lg:rounded-none bg-gradient-to-r from-teal-500 to-blue-500 lg:bg-none' style={{ minHeight: '500px' }} />
            <div className='lg:w-1/12' />
            <div className='lg:w-5/12 mb-5 lg:-mt-10 lg:pl-8' style={{ minHeight: '500px' }} />
          </div>
        ) : (
          <div className='flex flex-col lg:flex-row'>
            {/* Left Column */}
            <div className='lg:w-1/2 text-white p-8 lg:py-10 mb-12 lg:mb-0 rounded-lg lg:rounded-none bg-gradient-to-r from-teal-500 to-blue-500 lg:bg-none'>
              <div className='flex justify-center mb-8'>
                <FontAwesomeIcon icon={faShieldAlt} className='w-24 h-24' />
              </div>

              <h2 className='text-xl mb-6 text-left'>
                {t('pages.sitter.buyBackgroundCheck.leftColumn.description')}
              </h2>

              <p className='font-bold mb-8 text-lg text-left'>{t('pages.sitter.buyBackgroundCheck.leftColumn.benefits')}</p>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8'>
                <div className='text-center'>
                  <div className='flex justify-center mb-4'>
                    <FontAwesomeIcon icon={faMoneyBillWave} className='w-20 h-20' />
                  </div>
                  <p className='px-4'>{t('pages.sitter.buyBackgroundCheck.leftColumn.noOtherFees')}</p>
                </div>

                <div className='text-center'>
                  <div className='flex justify-center mb-4'>
                    <FontAwesomeIcon icon={faCertificate} className='w-20 h-20' />
                  </div>
                  <p className='px-4'>{t('pages.sitter.buyBackgroundCheck.leftColumn.background')}</p>
                </div>

                <div className='text-center'>
                  <div className='flex justify-center mb-4'>
                    <FontAwesomeIcon icon={faTools} className='w-20 h-20' />
                  </div>
                  <p className='px-4'>{t('pages.sitter.buyBackgroundCheck.leftColumn.improved')}</p>
                </div>

                <div className='text-center'>
                  <div className='flex justify-center mb-4'>
                    <FontAwesomeIcon icon={faHandsHelping} className='w-20 h-20' />
                  </div>
                  <p className='px-4'>{t('pages.sitter.buyBackgroundCheck.leftColumn.assistance')}</p>
                </div>
              </div>

              <div className='text-left'>
                <p className='font-bold mb-3'>
                  {t('pages.sitter.buyBackgroundCheck.leftColumn.membershipCost')} {defaultPlan?.feeLabel}
                </p>

                <p className='text-sm lg:text-base'>
                  {t('pages.sitter.buyBackgroundCheck.leftColumn.purchase')}{' '}
                  <a
                    href='https://support.urbansitter.com/hc/en-us/articles/360034613433'
                    className='text-white underline opacity-90 hover:opacity-100 font-bold'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t('pages.sitter.buyBackgroundCheck.leftColumn.learnMore')}
                  </a>
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className='lg:w-1/12' />
            <div className='lg:w-5/12 mb-5 lg:-mt-10 lg:pl-8'>
              {isModerationHold && (
                <div className='mt-8'>
                  <NotificationBanner
                    icon={faEyeSlash}
                    title={t('components.hiddenReasonBanner.decision.heading')}
                    body={t('components.hiddenReasonBanner.decision.body')}
                    color={colors.gray[400]}
                  />
                </div>
              )}

              {creditCard?.id ? (
                <div className='bg-gray-100 mt-8 mb-8 p-6 rounded-lg' style={{ minHeight: '300px' }}>
                  <div className='text-2xl font-bold mb-5'>{t('pages.sitter.buyBackgroundCheck.rightColumn.join')}</div>

                  <div className='mb-5 flex justify-between items-center'>
                    <div>{getCardLabel(creditCard?.type || '')}: {creditCard?.number}</div>
                    <div
                      id='remove-existing-card'
                      className='cursor-pointer text-lg font-bold'
                      onClick={handleDeleteCard}
                      onKeyDown={handleKeyDown}
                      role='button'
                      tabIndex={isCardRemovalDisabled ? -1 : 0}
                      aria-disabled={isCardRemovalDisabled}
                      style={{ opacity: isCardRemovalDisabled ? 0.5 : 1 }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </div>
                  </div>

                  <div className='mb-5'>
                    {t('pages.sitter.buyBackgroundCheck.rightColumn.annualMembership')} <span className='float-right font-bold'>$ {getSubtotal().toFixed(2)}</span>
                  </div>

                  {(defaultPlan?.masterServiceAreaDiscount || 0) > 0 && (
                    <div className='mb-5'>
                      {t('pages.sitter.buyBackgroundCheck.rightColumn.todayOnlyDiscount')} <span className='float-right font-bold'>-$ {getMsaDiscount().toFixed(2)}</span>
                    </div>
                  )}

                  {!defaultPlan?.discount && (
                    <div className='flex items-center mb-5'>
                      <div className='w-1/3'>{t('pages.sitter.buyBackgroundCheck.rightColumn.applyDiscount')}</div>
                      <div className='w-1/2 px-2' style={{ minHeight: '40px' }}>
                        <input
                          id='voucher-input'
                          className='w-full h-10 border border-gray-300 rounded px-3'
                          type='text'
                          maxLength={255}
                          placeholder={t('pages.sitter.buyBackgroundCheck.rightColumn.promoCode')}
                          value={voucherCode}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setVoucherCode(e.target.value)}
                        />
                      </div>
                      <div className='w-1/6' style={{ minHeight: '40px' }}>
                        <Button
                          id='voucher-apply'
                          buttonType={ButtonType.secondary}
                          buttonSize={ButtonSize.small}
                          onClick={redeemVoucher}
                          disabled={isVoucherRedemptionDisabled}
                        >
                          {t('pages.sitter.buyBackgroundCheck.rightColumn.applyButton')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {(defaultPlan?.discount || 0) > 0 && (
                    <div className='mb-5'>
                      {getDiscountCode()}<span className='float-right font-bold'>-$ {getDiscount().toFixed(2)}</span>
                    </div>
                  )}

                  <div className='font-bold'>
                    {t('pages.sitter.buyBackgroundCheck.rightColumn.total')} <span className='float-right'>$ {getTotal()}</span>
                  </div>
                </div>
              ) : shouldShowForm && (
                <>
                  <div className='bg-gray-100 mt-8 mb-8 p-6 rounded-lg' style={{ minHeight: '300px' }}>
                    <div className='text-2xl font-bold mb-5'>{t('pages.sitter.buyBackgroundCheck.rightColumn.join')}</div>

                    <div className='mb-5'>
                      {t('pages.sitter.buyBackgroundCheck.rightColumn.annualMembership')} <span className='float-right font-bold'>$ {getSubtotal().toFixed(2)}</span>
                    </div>

                    {(defaultPlan?.masterServiceAreaDiscount || 0) > 0 && (
                      <div className='mb-5'>
                        {t('pages.sitter.buyBackgroundCheck.rightColumn.todayOnlyDiscount')} <span className='float-right font-bold'>-$ {getMsaDiscount().toFixed(2)}</span>
                      </div>
                    )}

                    {!defaultPlan?.discount && (
                      <div className='flex items-center mb-5'>
                        <div className='w-1/3'>{t('pages.sitter.buyBackgroundCheck.rightColumn.applyDiscount')}</div>
                        <div className='w-1/2 px-2' style={{ minHeight: '40px' }}>
                          <input
                            id='voucher-input'
                            className='w-full h-10 border border-gray-300 rounded px-3'
                            type='text'
                            maxLength={255}
                            placeholder={t('pages.sitter.buyBackgroundCheck.rightColumn.promoCode')}
                            value={voucherCode}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setVoucherCode(e.target.value)}
                          />
                        </div>
                        <div className='w-1/6' style={{ minHeight: '40px' }}>
                          <Button
                            id='voucher-apply'
                            buttonType={ButtonType.secondary}
                            buttonSize={ButtonSize.small}
                            onClick={redeemVoucher}
                            disabled={isVoucherRedemptionDisabled}
                          >
                            {t('pages.sitter.buyBackgroundCheck.rightColumn.applyButton')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {(defaultPlan?.discount || 0) > 0 && (
                      <div className='mb-5'>
                        {getDiscountCode()}<span className='float-right font-bold'>-$ {getDiscount().toFixed(2)}</span>
                      </div>
                    )}

                    <div className='font-bold'>
                      {t('pages.sitter.buyBackgroundCheck.rightColumn.total')} <span className='float-right'>$ {getTotal()}</span>
                    </div>
                  </div>

                  <div className='mt-8'>
                    <div className='text-2xl font-bold'>{t('pages.sitter.buyBackgroundCheck.rightColumn.cardDetails')}</div>
                    <div className='flex justify-end items-center text-gray-500 text-sm'>
                      <span className='mr-2'>{t('pages.sitter.buyBackgroundCheck.rightColumn.securedBy')}</span>
                      <img src={LOCK_ICON_URL} alt='Security lock' className='w-4 h-4' width='16' height='16' />
                    </div>
                  </div>

                  <div
                    className='mt-4 p-4 border border-gray-200 rounded'
                    style={{
                      minHeight: '250px',
                      willChange: 'contents',
                      contain: 'layout'
                    }}
                  >
                    <PaymentElement id='payment-element' options={paymentElementOptions} onChange={handlePaymentElementChange} />
                  </div>
                </>
              )}

              <div className='text-sm mt-5'>
                <div className='flex items-start space-x-3'>
                  <input
                    id='terms-acceptance-checkbox'
                    type='checkbox'
                    checked={termsAccepted}
                    onChange={(e): void => setTermsAccepted(e.target.checked)}
                    className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                    required
                  />
                  <label htmlFor='terms-acceptance-checkbox'>
                    {t('pages.sitter.buyBackgroundCheck.rightColumn.termsAcceptance')}
                  </label>
                </div>

                <div className='mt-3 ml-7'>
                  <a
                    href='/terms-of-service'
                    className='font-bold'
                    style={{ color: colors.sapphire.DEFAULT }}
                    target='_blank'
                    aria-label='View Terms of Service in a new window'
                  >
                    {t('pages.sitter.buyBackgroundCheck.rightColumn.renewalTermsDescription2')}
                  </a>
                </div>
              </div>

              <div className={`text-center ${isNux ? 'mt-12' : 'mt-8'} mb-4`}>
                {isNux ? (
                  <>
                    <Button
                      id='skip-membership-nux'
                      buttonType={ButtonType.tertiary}
                      onClick={handleSkip}
                      disabled={isSubmitting}
                      className='mt-2 mb-4 mr-4 text-base'
                      style={{ color: 'black', fontSize: '16px' }}
                    >
                      {t('pages.sitter.buyBackgroundCheck.rightColumn.skipButton')}
                    </Button>
                    <Button
                      id='purchase-membership-nux'
                      buttonType={ButtonType.primary}
                      onClick={handlePurchase}
                      disabled={!canSubmit()}
                      className='mt-2 mb-4'
                      style={canSubmit() ? { backgroundColor: colors.emerald.DEFAULT } : undefined}
                    >
                      {t('pages.sitter.buyBackgroundCheck.rightColumn.purchaseButton')}
                    </Button>
                    <br />
                    <br />
                    <div className='flex justify-center space-x-2'>
                      {Array.from({ length: 7 }, (_, i) => (
                        <FontAwesomeIcon
                          key={`progress-dot-${i}`}
                          icon={faCircle}
                          className='w-4 h-4 text-gray-300'
                        />
                      ))}
                      <FontAwesomeIcon icon={faCircle} className='w-4 h-4 text-blue-500' />
                    </div>
                  </>
                ) : (
                  <>
                    <div className='flex justify-center space-x-2'>
                      <FontAwesomeIcon icon={faCheckCircle} className='w-4 h-4 text-green-500' />
                      <FontAwesomeIcon icon={faCircle} className='w-4 h-4 text-gray-300' />
                    </div>
                    <br />
                    <Button
                      id='purchase-membership'
                      buttonType={ButtonType.primary}
                      onClick={handlePurchase}
                      disabled={!canSubmit()}
                      className='mt-2 mb-4'
                      style={canSubmit() ? { backgroundColor: colors.emerald.DEFAULT } : undefined}
                    >
                      {t('pages.sitter.buyBackgroundCheck.rightColumn.purchaseButton')}
                    </Button>
                  </>
                )}
              </div>

              <div className='text-sm mt-5'>
                <div className='font-semibold mb-2 text-base'>{t('pages.sitter.buyBackgroundCheck.rightColumn.renewalTerms')}</div>
                <p className='mb-3'>
                  {t('pages.sitter.buyBackgroundCheck.rightColumn.renewalTermsDescription')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyBackgroundCheckPage;
